import { useState } from "react";
import { Bell } from "lucide-react";
import { toast } from "@/lib/toast";

import { useQueryClient } from "@tanstack/react-query";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import apiClient from "@/lib/api-client";
import { useActAsStore } from "@/stores/act-as-store";
import {
  useAckAnnouncement,
  useAnnouncementHistory,
  type AnnouncementHistoryItem,
} from "@/hooks/useAnnouncements";

import { AnnouncementDetailDialog } from "./AnnouncementDetailDialog";
import { Button } from "@/components/ui/button";

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function AnnouncementsBell() {
  const { data: history, isError: historyFailed, refetch: refetchHistory } = useAnnouncementHistory();
  const ack = useAckAnnouncement();
  const qc = useQueryClient();
  const isImpersonating = useActAsStore(
    (s) => s.actingAsUserId !== null,
  );
  const actingAsEmail = useActAsStore((s) => s.actingAsEmail);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] =
    useState<AnnouncementHistoryItem | null>(null);
  const [markingAll, setMarkingAll] = useState(false);

  const unreadCount = (history ?? []).filter((a) => !a.is_read).length;

  async function handleMarkAllRead() {
    const ids = (history ?? []).filter((a) => !a.is_read).map((a) => a.id);
    if (ids.length === 0) return;
    setMarkingAll(true);
    const results = await Promise.allSettled(
      ids.map((id) => apiClient.post(`api/announcements/${id}/ack`)),
    );
    const fulfilled = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - fulfilled;
    if (failed > 0) {
      toast.warning(
        `Marked ${fulfilled} of ${ids.length} as read — retry to clear the rest.`,
      );
    }
    // Invalidate both unread + history caches so the badge disappears
    // and the list re-renders with is_read=true. Going through React
    // Query (not raw apiClient.get) ensures every subscriber refetches.
    await qc.invalidateQueries({ queryKey: ["announcements"] });
    setMarkingAll(false);
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="relative rounded p-1 hover:bg-muted transition-colors"
            aria-label="Announcements"
          >
            <Bell className="size-4" />
            {unreadCount > 0 && (
              <span
                data-testid="announcements-bell-badge"
                className="absolute top-0 right-0 size-2 rounded-full bg-destructive"
              />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          sideOffset={6}
          className="w-80 p-0 text-sm"
        >
          <div className="max-h-[70vh] overflow-y-auto">
            {historyFailed && !history ? (
              <div className="p-4 text-center text-muted-foreground">
                <p className="mb-2">Couldn't load announcements.</p>
                <Button variant="outline" size="xs" onClick={() => void refetchHistory()}>
                  Try again
                </Button>
              </div>
            ) : !history || history.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground">
                No announcements yet.
              </div>
            ) : (
              history.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => setSelected(a)}
                  className={
                    "w-full text-left px-3 py-2 hover:bg-muted border-b " +
                    "last:border-0 flex items-start justify-between gap-2"
                  }
                >
                  <div className="min-w-0 flex-1">
                    <div
                      className={
                        "truncate " +
                        (a.is_read ? "" : "font-semibold")
                      }
                    >
                      {a.title}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {relativeTime(a.created_at)}
                    </div>
                  </div>
                  {!a.is_read && (
                    <span className="size-2 rounded-full bg-destructive shrink-0 mt-1.5" />
                  )}
                </button>
              ))
            )}
          </div>
          {(history ?? []).length > 0 && (
            <div className="border-t p-2">
              <button
                type="button"
                disabled={
                  isImpersonating ||
                  markingAll ||
                  unreadCount === 0
                }
                onClick={handleMarkAllRead}
                title={
                  isImpersonating
                    ? "Cannot ack while impersonating."
                    : undefined
                }
                className={
                  "w-full text-center text-xs py-1.5 rounded " +
                  (isImpersonating || unreadCount === 0
                    ? "text-muted-foreground cursor-not-allowed"
                    : "hover:bg-muted text-foreground")
                }
              >
                {markingAll ? "Marking…" : "Mark all read"}
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      <AnnouncementDetailDialog
        announcement={selected}
        open={!!selected}
        readOnly={isImpersonating}
        readOnlyReason={
          isImpersonating && actingAsEmail
            ? `Acting as ${actingAsEmail} — stop impersonation to acknowledge.`
            : isImpersonating
              ? "Impersonating — stop impersonation to acknowledge."
              : undefined
        }
        onClose={(acked) => {
          const current = selected;
          setSelected(null);
          if (acked && current && !isImpersonating) {
            ack.mutate(current.id);
          }
        }}
      />
    </>
  );
}
